import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { theme } from '../../styles/theme';

const HeroSection = styled.section`
  min-height: 100vh;
  background: ${theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 80px; /* Account for fixed navbar */
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing['4xl']};
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: ${theme.spacing['2xl']};
    padding: ${theme.spacing['4xl']} ${theme.spacing.xl};
  }
`;

const HeroContent = styled.div`
  color: ${theme.colors.white};
  z-index: 1;
`;

const HeroTitle = styled(motion.h1)`
  font-size: ${theme.typography.sizes['6xl']};
  font-weight: ${theme.typography.weights.bold};
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.1;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    font-size: ${theme.typography.sizes['5xl']};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.sizes['4xl']};
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${theme.typography.sizes.xl};
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: ${theme.spacing['sm']};
  max-width: 500px;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    margin: 0 auto ${theme.spacing['2xl']} auto;
  }
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const HeroVisual = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    order: -1;
  }
`;

const LogoContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: ${theme.spacing['xl']};
  
  img {
    width: 100%;
    height: auto;
    max-width: 600px;
    max-height: 400px;
    object-fit: contain;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.xl};
    img {
      max-width: 300px;
      max-height: 300px;
    }
  }
`;
const FloatingElement = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  backdrop-filter: blur(10px);
`;

const FloatingElement1 = styled(FloatingElement)`
  width: 100px;
  height: 100px;
  top: 20%;
  right: 10%;
`;

const FloatingElement2 = styled(FloatingElement)`
  width: 60px;
  height: 60px;
  bottom: 30%;
  left: 5%;
`;

const FloatingElement3 = styled(FloatingElement)`
  width: 80px;
  height: 80px;
  top: 60%;
  right: 20%;
`;

export const Hero: React.FC = () => {
  return (
    <HeroSection>
      <FloatingElement1
        animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <FloatingElement2
        animate={{
          y: [0, 15, 0],
          x: [0, 10, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <FloatingElement3
        animate={{
          y: [0, -25, 0],
          rotate: [0, -3, 0],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      <HeroContainer>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Stress-Free Care 
            <br></br>
            Anytime, Any Day
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Our care never sleeps - so you can.
            With real-time patient tracking, seamless communication, data-driven insights and more, Care++ is a gateway between patients and workers to ensure loved ones receive the best care, every day.
            <br></br> 
            <br></br> 
            Join the future of care, request a demo today!
          </HeroSubtitle>
          <HeroButtons
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Button 
              size="lg" 
              variant="ghost" 
              icon={<Icon icon={FiArrowRight} />}
              onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Join Waitlist
            </Button>
            <Link to="/patient-dashboard" style={{ textDecoration: 'none' }}>
              <Button size="lg" variant="outline">
                View Demo Dashboard
              </Button>
            </Link>
          </HeroButtons>
        </HeroContent>
        
        <HeroVisual>
          <LogoContainer
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0 }}
            whileHover={{ scale: 1.5}}
          >
            <img src="/dashboard.png" alt="Patient Dashboard"/>
          </LogoContainer>
        </HeroVisual>
      </HeroContainer>
    </HeroSection>
  );
};